<template>
  <va-card :title="$t('menu.data_integrations')">
    <div class="row">

      <div class="flex xs12 sm2">
        <va-date-picker
          :label="$t('tables.date')"
          v-model="integration_date"
        />
      </div>

      <div class="flex">
        <va-button
          small
          color="secondary"
          icon="fa fa-eraser"
          class="ma-2"
          @click="integration_date = ''"
        >
          {{ $t('menu.clear') }}
        </va-button>
      </div>

      <div class="flex md1">
        <va-button
          small
          color="secondary"
          icon="fa fa-search"
          class="ma-2"
          @click="readItems(page, integration_date)"
        >
          {{ $t('menu.search') }}
        </va-button>
      </div>

      <div class="flex xs12 md2 offset--md6">
        <va-select
          v-model="perPage"
          :value="perPage"
          :label="$t('tables.perPage')"
          :options="perPageOptions"
        />
      </div>
    </div>

    <div v-show="loading" class="text--center pb-4">
      <div class="flex-center spinner-box">
        <slot name="loading">
          <spring-spinner
            slot="loading"
            :animation-duration="currentDuration"
            :is="spload.name"
            :color="spinnersColor"
            :size="config.size"
          />
        </slot>
      </div>
    </div>

    <va-data-table
      v-if="renderComponent"
      :fields="fields"
      :data="data"
      :per-page="parseInt(perPage)"
      :total-pages="parseInt(totalPages)"
      v-show="!loading"
      no-pagination
      api-mode
      @page-selected="readItems"
      :row-class="computedRowClass"
    >

    <template slot="actions" slot-scope="props">
        <va-button v-if="props.rowData.finished === false"
          small
          inline
          color="danger"
          icon="fa fa-check"
          class="ma-0"
          @click="showModalFinalize = true, rowId = props.rowData.id"
        >
          {{ $t('tables.data_integrations.finalize') }}
      </va-button>

      <va-button v-if="props.rowData.loads_quantity >= 1"
          small
          inline
          color="green"
          icon="fa fa-list"
          class="ma-0"
          :to="{
            name: 'loads_by_data_integration',
            params: {
              dataIntegrationId: props.rowData.id}
          }"
        >
          {{ $t('tables.data_integrations.loads') }}
      </va-button>
      </template>
    </va-data-table>

    <Pagination
      :currentPage="current_page"
      :totalPages="totalPages"
      :onChange="onPageChange"
      v-show="!loading"
    />

    <va-modal
        v-model="showModalFinalize"
        size="small"
        :title="$t('modal.finalize')"
        :message="$t('modal.messageFinalzeDataIntegration')"
        hideDefaultActions
        :color="this.$themes.secondary"
      >

      <va-button
        color="danger"
        class="ma-2"
        @click="showModalFinalize = false">
        {{$t('modal.cancel')}}
      </va-button>

      <va-button
        color="green"
        class="ma-2"
        @click="finalizeDataIntegration(rowId), showModalFinalize = false">
        {{$t('modal.confirm')}}
      </va-button>
    </va-modal>

  </va-card>
</template>

<script>
import { mapState } from 'vuex'
import { Ability } from '@casl/ability'
import DataIntegrationAPI from '@/services/DataIntegrationAPI'
import * as spinners from 'epic-spinners'
import Pagination from '../shared/Pagination'

export const ability = new Ability()

export default {
  name: 'modals',
  components: {
    ...spinners,
    Pagination,
  },
  data () {
    return {
      showModalFinalize: false,
      integration_date: '',
      page: 1,
      rowId: '',
      data: [],
      loading: false,
      config: {
        size: 80,
        group: 4,
        duration: 1500,
      },
      spinnersColor: this.$themes.secondary,
      currentDuration: 1500,
      showModal: false,
      ability: [],
      codeLoad: '',
      perPage: '10',
      totalPages: 0,
      perPageOptions: ['10', '20', '30', '50'],
      search: '',
      renderComponent: true,
      current_page: '',
      last_refresh: new Date(),
    }
  },

  channels: {
    NeedRefreshDataIntegrations: {
      received (data) {
        this.readItems(this.currentPage)
      },
    },
  },

  beforeMount () {
    this.ability = ability.update(this.rules)
  },

  mounted () {
    this.$cable.subscribe({ channel: 'NeedRefreshDataIntegrations', company: this.currentUser.company })
    this.readIntegrationDate(this.currentLocality.id)
  },

  computed: {
    ...mapState(['rules', 'currentLocality', 'currentUser']),

    fields () {
      return [{
        name: 'id',
        title: this.$t('tables.data_integrations.id'),
      },
      {
        name: 'locality_name',
        title: this.$t('tables.data_integrations.locality'),
      },
      {
        name: 'created_at',
        title: this.$t('tables.data_integrations.created_at'),
      },
      {
        name: 'updated_at',
        title: this.$t('tables.data_integrations.updated_at'),
      },
      {
        name: 'duration',
        title: this.$t('tables.data_integrations.duration'),
      },
      {
        name: 'loads_quantity',
        title: this.$t('tables.data_integrations.loads_quantity'),
      },
      {
        name: 'average_duration_per_loads',
        title: this.$t('tables.data_integrations.average_duration_per_loads'),
      },
      {
        name: '__slot:actions',
        dataClass: 'text-right',
      }]
    },
    spload () {
      return spinners.ScalingSquaresSpinner
    },
  },
  watch: {
    perPage: function (newVal) {
      this.perPage = newVal
      this.readItems(this.currentPage)
    },
    integration_date: function () {
      this.readItems(this.currentPage)
    },

    '$store.state.currentLocality': function () {
      this.readItems(this.currentPage)
    },
  },
  methods: {
    onPageChange (page) {
      this.readItems(page)
    },

    async readItems (page = 1) {
      this.loading = true

      const params = {
        locality_id: this.currentLocality.id,
        page: page,
        quantity: this.perPage,
        date: this.integration_date,
      }
      this.current_page = this.$route.params.current_page

      await DataIntegrationAPI.getDataIntegrationsByLocality(params).then(response => {
        this.totalPages = response.data.total_pages
        this.current_page = response.data.current_page
        this.data = response.data.data_integrations
        this.loading = false
      })
    },

    finalizeDataIntegration (id) {
      DataIntegrationAPI.finalizeDataIntegration(id).then(response => {
        this.showToast(response.data.message, {
          icon: 'fa-thumbs-o-up',
          position: 'top-right',
          duration: 5000,
        })
        this.readItems(this.currentPage)
      }).catch(e => {
        this.showToast(e.response.data.error, {
          icon: 'fa-thumbs-o-down',
          position: 'top-right',
          duration: 5000,
        })
      })
    },

    async readIntegrationDate (localityId) {
      await DataIntegrationAPI.getLastDataIntegrationDate(localityId).then(response => {
        this.integration_date = response.data.last_data_integration_date
        this.readItems(this.currentPage)
      }).catch(e => {
        this.showToast(e.response.data.error, {
          icon: 'fa-thumbs-o-down',
          position: 'top-right',
          duration: 5000,
        })
      })
    },

    clearSearch () {
      this.search = ''
    },

    computedRowClass (rowData) {
      if (rowData.finished === false) {
        return 'bold-row'
      } else {
        return 'normal-row'
      }
    },
  },
}
</script>

<style>
  .bold-row {
    font-weight: bold;
  }
</style>
